import { getHistoryData } from "@/apis/wallet";
import { getToken } from "@/utils/auth";
import { getWsUrl } from "./websocket";
// import dayjs from "dayjs";

interface SymbolInfo {
  // 唯一标识
  ticker: string;

  // 标的名
  name?: string;

  // 标的简短名
  shortName?: string;

  // 交易所
  exchange?: string;

  // 市场分类
  market?: string;

  // 价格精度
  pricePrecision?: number;

  // 数量精度
  volumePrecision?: number;

  // 价格币种
  priceCurrency?: string;

  // 类型
  type?: string;

  // logo url
  logo?: string;
}

interface Period {
  multiplier: number;
  timespan: string;
  text: string;
}

interface KLineData {
  timestamp: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

type DatafeedSubscribeCallback = (data: KLineData) => void;

// const keys: any = {};

export class CustomDatafeed {
  private ws: WebSocket | null = null;
  private callbacks = new Map<string, DatafeedSubscribeCallback>();
  private heartbeatTimer: any = null;
  private currentSymbol: SymbolInfo | null = null;
  private currentPeriod: Period | null = null;
  private currentCallback: DatafeedSubscribeCallback | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimeout = 1000;
  private disabledReconnect = false;
  private wsUrl = getWsUrl();
  private isConnecting = false;
  private disableRT = false;

  // 静态实例追踪
  private static instances: Set<CustomDatafeed> = new Set();

  constructor() {
    CustomDatafeed.instances.add(this);
  }

  // 静态清理方法
  public static cleanupAll(): void {
    CustomDatafeed.instances.forEach((instance) => {
      instance.close();
    });
    CustomDatafeed.instances.clear();
  }

  private async ensureConnection(): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    if (this.isConnecting) {
      // 等待连接完成
      await new Promise<void>((resolve) => {
        const checkConnection = () => {
          if (!this.isConnecting) {
            resolve();
          } else {
            setTimeout(checkConnection, 100);
          }
        };
        checkConnection();
      });
      return;
    }

    this.isConnecting = true;

    return new Promise((resolve, reject) => {
      this.ws = new WebSocket(this.wsUrl);

      this.ws.onopen = () => {
        console.log("K线 WebSocket connected");
        this.reconnectAttempts = 0;
        this.disabledReconnect = false;
        this.isConnecting = false;
        resolve();
      };

      this.ws.onerror = (error) => {
        console.error("K线 WebSocket error:", error);
        this.isConnecting = false;
        reject(error);
      };

      this.ws.onclose = () => {
        console.log("K线 WebSocket closed");
        this.stopHeartbeat();
        this.isConnecting = false;
        if (!this.disabledReconnect) {
          this.reconnect();
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (
            data.ch == "sub.bizhong@item@1M" &&
            this.currentCallback &&
            this.disableRT
          ) {
            this.disableRT = false;
            if (this.currentSymbol && this.currentPeriod) {
              this.startHeartbeat(
                this.currentSymbol.ticker,
                this.currentPeriod.text
              );
            }
            const kline = data.data;
            this.currentCallback({
              timestamp: kline.timestamp,
              open: kline.open,
              high: kline.high,
              low: kline.low,
              close: kline.close,
              volume: kline.volume,
            });
            return;
          }
          if (data.ch === "sub.bizhong@item@RT" && this.currentCallback) {
            if (this.disableRT) {
              return;
            }

            // const key = dayjs(data.data[0].timestamp).format("YYYY-MM-DD HH:mm:ss") + "," + dayjs(data.data[1].timestamp).format("YYYY-MM-DD HH:mm:ss")
            // console.log(key)
            // if (!keys[key]) {
            //   console.log("新时间", data.data)
            //   keys[key] = data.data;
            // }
            const kline = data.data;
            this.currentCallback({
              timestamp: kline.timestamp,
              open: kline.open,
              high: kline.high,
              low: kline.low,
              close: kline.close,
              volume: kline.volume,
            });
            // const kline2 = data.data[1];
            // this.currentCallback({
            //   timestamp: kline2.timestamp,
            //   open: kline2.open,
            //   high: kline2.high,
            //   low: kline2.low,
            //   close: kline2.close,
            //   volume: kline2.volume,
            // });
          }
        } catch (error) {
          console.error("Failed to process websocket data:", error);
        }
      };
    });
  }

  private reconnect() {
    if (this.disabledReconnect) {
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log("k线图ws达到最大重连次数");
      return;
    }

    this.stopHeartbeat();
    this.reconnectAttempts++;
    console.log(`K线图ws重连中... Attempt ${this.reconnectAttempts}`);

    setTimeout(async () => {
      if (this.currentSymbol && this.currentPeriod && this.currentCallback) {
        await this.ensureConnection();
        this.startHeartbeat(this.currentSymbol.ticker, this.currentPeriod.text);
      }
    }, this.reconnectTimeout);
  }

  private startHeartbeat(symbol: string, period: string) {
    this.stopHeartbeat();
    let lastMessageTime = Date.now();
    this.heartbeatTimer = setInterval(() => {
      if (Date.now() - lastMessageTime > 3000) {
        console.log("k线图ws重连中...");
        this.reconnect();
        return;
      }
      if (this.ws?.readyState === WebSocket.OPEN) {
        const curDate = new Date();
        if (curDate.getSeconds() == 0) {
          this.stopHeartbeat();
          this.disableRT = true;
          const message = {
            event: "sub.bizhong@item",
            data: {
              ename: this.currentSymbol?.ticker,
              time_frames: "1M",
              token: getToken() || "",
              timestamp: curDate.getTime(),
            },
          };
          this.ws?.send(JSON.stringify(message));
          return;
        }
        const message = {
          event: "sub.bizhong@item",
          data: {
            ename: symbol,
            time_frames: "RT" || period,
            token: getToken() || "",
            timestamp: curDate.getTime(),
          },
        };
        this.ws.send(JSON.stringify(message));
        lastMessageTime = Date.now();
      }
    }, 500);
  }

  private stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * 模糊搜索标的
   * 在搜索框输入的时候触发
   * 返回标的信息数组
   */
  async searchSymbols(search?: string): Promise<SymbolInfo[]> {
    // 这里可以返回固定的交易对列表或者调用API获取
    return [
      {
        ticker: "BTCUSDT",
        name: "Bitcoin",
        shortName: "BTC",
        exchange: "Binance",
        market: "Crypto",
        priceCurrency: "USDT",
        type: "Crypto",
      },
    ];
  }

  /**
   * 获取历史k线数据
   * 当标的和周期发生变化的时候触发
   *
   * 返回标的k线数据数组
   */
  async getHistoryKLineData(
    symbol: SymbolInfo,
    period: Period,
    from: number,
    to: number
  ): Promise<KLineData[]> {
    try {
      console.log("时间", from, to);
      const res = await getHistoryData({
        symbol: symbol.ticker,
        interval: period.text,
        limit: 1000,
        from: from,
        to: to,
      });
      if (res.code == 0) {
        return res.data.map((item: any) => ({
          timestamp: item.timestamp,
          open: item.open,
          high: item.high,
          low: item.low,
          close: item.close,
          volume: item.volume,
        }));
      }
      return [];
    } catch (error) {
      console.error("Failed to fetch history data:", error);
      return [];
    }
  }

  /**
   * 订阅K线数据
   */
  async subscribe(
    symbol: SymbolInfo,
    period: Period,
    callback: DatafeedSubscribeCallback
  ): Promise<void> {
    // 如果是相同的订阅，直接返回
    if (
      this.currentSymbol?.ticker === symbol.ticker &&
      this.currentPeriod?.text === period.text
    ) {
      return;
    }
    this.disableRT = false;
    // 停止当前的心跳
    this.stopHeartbeat();

    // 更新当前的订阅信息
    this.currentSymbol = symbol;
    this.currentPeriod = period;
    this.currentCallback = callback;

    // 确保 WebSocket 连接
    await this.ensureConnection();

    // 开始新的心跳
    this.startHeartbeat(symbol.ticker, period.text);

    // 更新回调映射
    this.callbacks.set(`${symbol.ticker}_${period.text}`, callback);
  }

  /**
   * 取消订阅
   */
  unsubscribe(symbol: SymbolInfo, period: Period): void {
    const key = `${symbol.ticker}_${period.text}`;
    this.callbacks.delete(key);

    // 如果取消订阅的是当前活跃的订阅，则停止心跳
    if (
      this.currentSymbol?.ticker === symbol.ticker &&
      this.currentPeriod?.text === period.text
    ) {
      this.stopHeartbeat();
      this.currentSymbol = null;
      this.currentPeriod = null;
      this.currentCallback = null;
      this.disableRT = false;
    }

    // 只有在明确要关闭连接时才关闭WebSocket
    if (this.disabledReconnect && this.ws) {
      this.ws.close();
      this.ws = null;
      this.disableRT = false;
    }
  }

  /**
   * 关闭WebSocket连接
   */
  close(): void {
    this.disabledReconnect = true;
    this.stopHeartbeat();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    CustomDatafeed.instances.delete(this);
  }
}

// 创建单例实例
export const customDatafeed = new CustomDatafeed();
